/**
 * Analytics consent - what the visitor decided, and remembering it.
 *
 * Storage is deliberately `localStorage` and not a cookie: a cookie would be sent on
 * every request to the origin for no reason, and the decision is only ever read by the
 * client. It is also the one piece of state that must survive the visitor saying "no" -
 * without it the banner would reappear on every page load and the refusal would be
 * indistinguishable from never having been asked.
 */

export type ConsentChoice = 'granted' | 'denied';

const STORAGE_KEY = 'kando.consent';

/**
 * Bump when what we ask permission for changes - a new vendor, a new category.
 * A stored decision from an older version is treated as no decision, so the visitor
 * is asked again rather than being opted in to something they never saw.
 */
export const CONSENT_VERSION = 1;

interface StoredConsent {
  readonly version: number;
  readonly analytics: ConsentChoice;
  readonly decidedAt: string;
}

function isConsentChoice(value: unknown): value is ConsentChoice {
  return value === 'granted' || value === 'denied';
}

/**
 * The visitor's stored decision, or `null` if they have not made one.
 *
 * Returns `null` rather than throwing when storage is unavailable - Safari's private
 * mode and a browser configured to block storage both make `localStorage` throw on
 * access. That is not an error condition to report; it means the same as "no decision
 * recorded", and the correct behaviour is identical: ask, and keep analytics denied
 * until told otherwise.
 */
export function readConsent(): ConsentChoice | null {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const { version, analytics } = parsed as Partial<StoredConsent>;

    // A decision recorded against a different question is not an answer to this one.
    if (version !== CONSENT_VERSION || !isConsentChoice(analytics)) {
      return null;
    }

    return analytics;
  } catch {
    // Corrupt or hand-edited value. Treat it as unanswered and ask again.
    return null;
  }
}

/**
 * Records the decision. Failing to persist it does not fail the decision itself -
 * the caller has already applied it to this page view.
 */
export function storeConsent(analytics: ConsentChoice): void {
  const value: StoredConsent = {
    version: CONSENT_VERSION,
    analytics,
    decidedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable or full. The banner will ask again next visit, which is the
    // conservative outcome - it cannot silently grant something never agreed to.
  }
}

/** Forgets the decision, so the banner asks again. Used by the privacy page. */
export function clearConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do - if storage cannot be written it holds no decision to clear.
  }
}
